import constants from "./constants";
import getApiHeaders from './apiHeaders.js';

const MESSAGE_STORAGE = {
    topic1: [{ sender: "system", content: "Message 1 for Topic 1" }, { sender: "system", content: "Message 2 for Topic 1" }],
    topic2: [{ sender: "system", content: "Message 1 for Topic 2" }],
};

export const addMessageToTopic = (topicKey, message) => {
    if (!MESSAGE_STORAGE[topicKey]) {
        MESSAGE_STORAGE[topicKey] = []; // Initialize the topic if it doesn't exist
    }
    MESSAGE_STORAGE[topicKey].push(message); // Add the new message
};

export const getMessagesByTopic = (topicKey) => MESSAGE_STORAGE[topicKey] || [];

function empty(array) {
    return array.length === 0;
}

// Helper function to use new chat endpoint for node chat
async function fetchChatWithNewSystem(chatId, message, topic, topic_, context = null, fileId = null) {
    const url = chatId 
        ? `${constants.fetchChatById}/${chatId}`
        : `${constants.fetchChatById}`;
    
    const requestBody = {
        message: message,
        topic: topic,
        topic_: topic_
    };
    
    // If fileId is provided and we have a chatId, use RAG search (send fileId + query)
    if (fileId && chatId && !context) {
        requestBody.fileId = fileId;
        requestBody.query = message; // Use message as query for RAG search
        console.log('[FETCH_CHAT_WITH_NEW_SYSTEM] Using RAG search with fileId:', fileId);
    } else if (context && Array.isArray(context) && context.length > 0) {
        // Add context if provided (backward compatible)
        requestBody.context = context;
        console.log('[FETCH_CHAT_WITH_NEW_SYSTEM] Adding context to request:', {
            contextLength: context.length,
            firstContextPreview: context[0] ? context[0].substring(0, 100) + '...' : null
        });
    } else {
        console.log('[FETCH_CHAT_WITH_NEW_SYSTEM] No context or fileId provided:', {
            context: context,
            isArray: Array.isArray(context),
            length: context ? context.length : 0,
            fileId: fileId,
            chatId: chatId
        });
    }
    
    console.log('[FETCH_CHAT_WITH_NEW_SYSTEM] Sending request:', {
        url: url,
        hasContext: !!(requestBody.context),
        contextInBody: !!requestBody.context
    });
    
    const response = await fetch(url, {
        method: 'POST',
        headers: getApiHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.error || errorData || 'Failed to fetch chat response');
        error.status = response.status;
        error.message = errorData.error || errorData || error.message;
        throw error;
    }

    const result = await response.json();
    
    // Response: { messages: [{ role, content, timestamp, references?, ... }], _id: string }
    // References are now attached directly to each message
    const messages = result.messages || [];
    
    return {
        messages: messages,
        _id: result._id
    };
}

const fetchNodeChat = async (lastUserMessage, lastBotMessage, topicKey, topic, chatId = null, context = null, fileId = null) => {
    if (!topicKey) {
        throw new Error("Topic key is required to fetch messages.");
    }

    // Use new system if:
    // 1. We have a user message, AND
    // 2. (We have a chatId OR we have context to send OR we have topic/topic_ for topic nodes OR we have fileId for RAG)
    // This ensures context is sent even on the first message, and topic nodes also get questions
    // Also enables RAG search when fileId is available
    const shouldUseNewSystem = lastUserMessage && 
                                lastUserMessage.role === 'user' && 
                                lastUserMessage.content &&
                                (chatId || (context && Array.isArray(context) && context.length > 0) || (topicKey && topic) || fileId);

    if (shouldUseNewSystem) {
        try {
            console.log('[FETCH_NODE_CHAT] Using new system:', {
                hasChatId: !!chatId,
                hasContext: !!(context && Array.isArray(context) && context.length > 0),
                contextLength: context ? context.length : 0,
                hasFileId: !!fileId
            });
            
            const result = await fetchChatWithNewSystem(
                chatId,
                lastUserMessage.content,
                topicKey,
                topic,
                context,
                fileId
            );
            
            // Update local storage
            var localMessages = getMessagesByTopic(topicKey);
            if (empty(localMessages)) {
                MESSAGE_STORAGE[topicKey] = [lastUserMessage];
            } else {
                if (lastUserMessage) {
                    localMessages.push(lastUserMessage);
                }
                MESSAGE_STORAGE[topicKey] = localMessages;
            }
            // Add assistant messages to local storage
            const assistantMsgs = result.messages.filter(m => m.role === 'assistant');
            localMessages = getMessagesByTopic(topicKey);
            assistantMsgs.forEach(msg => localMessages.push(msg));
            MESSAGE_STORAGE[topicKey] = localMessages;
            
            return {
                messages: result.messages,
                reinforcementLabel: 0, // Not available in new system yet
                _id: result._id
            };
        } catch (error) {
            console.warn('[FETCH_NODE_CHAT] Error using new system, falling back to old system:', error);
            // Fall through to old system
        }
    }

    // Old system (fallback)
    var messages = getMessagesByTopic(topicKey)
    if (empty(messages)) {
        MESSAGE_STORAGE[topicKey] = [lastUserMessage]
    } else {
        // if (lastBotMessage) {
        //     messages.push(lastBotMessage)
        // }

        if (lastUserMessage) {
            messages.push(lastUserMessage)
        }

        MESSAGE_STORAGE[topicKey] = messages
    }

    messages = getMessagesByTopic(topicKey)

    const response = await fetch(
        `${constants.fetchNodeChat}`,
        {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify({
                topic_: topic,
                topic: topicKey,
                messages: messages
            }),
            credentials: "include"
        }
    );

    const message = await response.json();

    const botMessage = {
        role: 'assistant',
        content: message.message || message,
        timestamp: new Date(),
        status: response.status
    };

    messages.push(botMessage)
    MESSAGE_STORAGE[topicKey] = messages

    return {
        message: botMessage,
        reinforcementLabel: message.reinforcementLabel || 0
    }
}

export default fetchNodeChat;
